import { AxiosError, AxiosRequestConfig } from 'axios';
import { useCallback, useState } from 'react';
import api, { camelizeKeys } from 'utils/axios';

interface FetchParams<Body = unknown> {
  url: string;
  method?: 'get' | 'post';
  body?: Body;
  options?: AxiosRequestConfig;
  onError?: (error: AxiosError) => void;
  camelizeResponse?: boolean;
}

/** Hook for lazy fetching data by axios. */
export const useLazyFetch = <Response, ErrorResponse = unknown, Body = unknown>() => {
  const [data, setData] = useState<Response | null>(null);
  const [error, setError] = useState<AxiosError<ErrorResponse> | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (params: FetchParams<Body>) => {
    const { camelizeResponse, url, method = 'get', body, options, onError } = params;

    setLoading(true);
    setError(null);

    try {
      const response =
        method === 'post' ? await api.post<Response>(url, body, options) : await api.get<Response>(url, options);

      const responseData = camelizeResponse ? camelizeKeys(response.data) : response.data;
      setData(responseData);

      return responseData;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<ErrorResponse>;

      setError(axiosError);
      onError?.(axiosError);

      throw axiosError;
    } finally {
      setLoading(false);
    }
  }, []);

  return { fetchData, data, error, loading };
};
